
import React, { useState, useEffect } from 'react'
import { Bell, AlertTriangle, Clock, DollarSign, X } from 'lucide-react'

interface NotificationBellObrasProps {
  obras: any[]
  solicitacoes: any[]
}

interface Notification {
  id: string
  type: 'orcamento_excedido' | 'etapa_atrasada' | 'etapa_iniciando' | 'solicitacao_material'
  title: string
  message: string
  obra: string
  priority: 'low' | 'medium' | 'high'
  createdAt: string
}

export const NotificationBellObras: React.FC<NotificationBellObrasProps> = ({
  obras,
  solicitacoes
}) => {
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    generateNotifications()
  }, [obras, solicitacoes])

  const generateNotifications = () => {
    const newNotifications: Notification[] = []

    // Verificar orçamentos excedidos
    obras.forEach(obra => {
      if (obra.valorRealizado > obra.valorOrcado) {
        const excesso = obra.valorRealizado - obra.valorOrcado
        const percentualExcesso = ((excesso / obra.valorOrcado) * 100).toFixed(1)
        
        newNotifications.push({
          id: `orcamento_${obra._id}`,
          type: 'orcamento_excedido',
          title: 'Orçamento Excedido',
          message: `A obra "${obra.nomeObra}" excedeu o orçamento em ${percentualExcesso}% (R$ ${excesso.toLocaleString('pt-BR', { minimumFractionDigits: 2 })})`,
          obra: obra.nomeObra,
          priority: excesso > obra.valorOrcado * 0.1 ? 'high' : 'medium',
          createdAt: new Date().toISOString()
        })
      }
    })

    // Verificar etapas atrasadas
    obras.forEach(obra => {
      obra.etapas?.forEach((etapa: any) => {
        if (etapa.status !== 'realizado' && etapa.dataTerminoPrevisto) {
          const dataTermino = new Date(etapa.dataTerminoPrevisto)
          const hoje = new Date()
          
          if (hoje > dataTermino) {
            const diasAtraso = Math.ceil((hoje.getTime() - dataTermino.getTime()) / (1000 * 60 * 60 * 24))
            
            newNotifications.push({
              id: `etapa_atrasada_${etapa.id}`,
              type: 'etapa_atrasada',
              title: 'Etapa Atrasada',
              message: `A etapa "${etapa.nome}" da obra "${obra.nomeObra}" está atrasada há ${diasAtraso} dias`,
              obra: obra.nomeObra,
              priority: diasAtraso > 7 ? 'high' : 'medium',
              createdAt: new Date().toISOString()
            })
          }
        }
      })
    })

    // Verificar etapas que vão iniciar
    obras.forEach(obra => {
      obra.etapas?.forEach((etapa: any) => {
        if (etapa.status === 'previsto' && etapa.dataInicioPrevisto) {
          const dataInicio = new Date(etapa.dataInicioPrevisto)
          const hoje = new Date()
          const amanha = new Date(hoje)
          amanha.setDate(hoje.getDate() + 1)
          const proximos3Dias = new Date(hoje)
          proximos3Dias.setDate(hoje.getDate() + 3)
          
          if (dataInicio >= hoje && dataInicio <= proximos3Dias) {
            const diasRestantes = Math.ceil((dataInicio.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24))
            
            newNotifications.push({
              id: `etapa_iniciando_${etapa.id}`,
              type: 'etapa_iniciando',
              title: 'Etapa Iniciando',
              message: `A etapa "${etapa.nome}" da obra "${obra.nomeObra}" deve iniciar em ${diasRestantes === 0 ? 'hoje' : diasRestantes === 1 ? 'amanhã' : `${diasRestantes} dias`}`,
              obra: obra.nomeObra,
              priority: diasRestantes <= 1 ? 'high' : 'medium',
              createdAt: new Date().toISOString()
            })
          }
        }
      })
    })

    // Verificar solicitações de material pendentes
    solicitacoes.forEach(solicitacao => {
      if (solicitacao.status === 'pendente') {
        const dataSolicitacao = new Date(solicitacao.dataSolicitacao)
        const hoje = new Date()
        const diasPendente = Math.ceil((hoje.getTime() - dataSolicitacao.getTime()) / (1000 * 60 * 60 * 24))
        
        if (diasPendente >= 2) {
          newNotifications.push({
            id: `solicitacao_${solicitacao._id}`,
            type: 'solicitacao_material',
            title: 'Solicitação Pendente',
            message: `Solicitação de "${solicitacao.nomeInsumo}" está pendente há ${diasPendente} dias`,
            obra: 'Material',
            priority: solicitacao.prioridade === 'urgente' || solicitacao.prioridade === 'alta' ? 'high' : 'medium',
            createdAt: new Date().toISOString()
          })
        }
      }
    })

    // Ordenar por prioridade e data
    newNotifications.sort((a, b) => {
      const priorityOrder = { high: 3, medium: 2, low: 1 }
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[b.priority] - priorityOrder[a.priority]
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    })

    setNotifications(newNotifications)
    setUnreadCount(newNotifications.length)
  }

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'orcamento_excedido':
        return <DollarSign size={16} className="text-red-500" />
      case 'etapa_atrasada':
        return <AlertTriangle size={16} className="text-red-500" />
      case 'etapa_iniciando':
        return <Clock size={16} className="text-yellow-500" />
      case 'solicitacao_material':
        return <AlertTriangle size={16} className="text-orange-500" />
      default:
        return <Bell size={16} className="text-blue-500" />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'border-l-red-500 bg-red-50'
      case 'medium':
        return 'border-l-yellow-500 bg-yellow-50'
      case 'low':
        return 'border-l-blue-500 bg-blue-50'
      default:
        return 'border-l-gray-500 bg-gray-50'
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))

    if (diffInMinutes < 1) return 'Agora'
    if (diffInMinutes < 60) return `${diffInMinutes}m atrás`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h atrás`
    return `${Math.floor(diffInMinutes / 1440)}d atrás`
  }

  const clearNotifications = () => {
    setNotifications([])
    setUnreadCount(0)
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border z-50">
          <div className="p-4 border-b">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Alertas de Obras</h3>
              <div className="flex items-center gap-2">
                {notifications.length > 0 && (
                  <button
                    onClick={clearNotifications}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Limpar tudo
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <Bell size={32} className="mx-auto mb-2 text-gray-300" />
                <p>Nenhum alerta no momento</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 border-l-4 ${getPriorityColor(notification.priority)}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start">
                          <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </p>
                          <span className="text-xs text-gray-500 ml-2">
                            {formatTimeAgo(notification.createdAt)}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {notification.obra}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t bg-gray-50 text-center">
              <p className="text-xs text-gray-500">
                {notifications.length} alerta{notifications.length !== 1 ? 's' : ''} ativo{notifications.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default NotificationBellObras
